import { Formik } from 'formik';
import type { ReactNode } from 'react';

import type { ApiOrgMarket } from '@kaiizen/kai-pro-api';
import { useScanTemplates } from '@kaiizen/kai-pro-api-react-state';
import { ErrorWithStatus } from '@kaiizen/zod-less-api';

import { useOrgContractors, useToast, useUsers, useUsersByOrgMarketId } from '../../State';
import { FlyOut, FormikLabeledInput, FormikLabeledMultiSelect, FormikLabeledSelect } from '../../Widgets';
import { DEFAULT_MARKET_PANEL_STYLES, type MarketPanelStyles } from './marketPanelStyles';

const isZipCodeValid = (zipCode: string) => {
  return zipCode.length === 5 && /^\d+$/.test(zipCode);
};

/** The Market drawer's two-line heading. */
const MarketPanelTitle = ({ marketName, styles }: { marketName: string; styles: MarketPanelStyles }) => (
  <div css={styles.title}>
    <span css={styles.titleEyebrow}>Market</span>
    <span css={styles.titleName}>{marketName}</span>
  </div>
);

/** A labeled market field, matching the external field labels from the new overview drawer. */
const MarketField = ({ children, label, optional = false, styles }: {
  children: ReactNode;
  label: string;
  optional?: boolean;
  styles: MarketPanelStyles;
}) => (
  <div css={styles.field}>
    <span css={styles.fieldLabel}>
      {label}
      {optional && <span css={styles.optionalIndicator}>Optional</span>}
    </span>
    {children}
  </div>
);

/** The market editor, shared by the Markets page and the project overview's linked market. */
export const MarketPanel = ({
  styles = DEFAULT_MARKET_PANEL_STYLES,
  flyoutOpen,
  closeFlyout,
  marketToEdit,
  updateMarket,
  addMarket,
  updateUsersForMarket,
  readOnly,
}: {
  styles?: MarketPanelStyles;
  flyoutOpen: boolean;
  closeFlyout: () => void;
  marketToEdit?: ApiOrgMarket;
  updateMarket: (
    marketId: string,
    name: string,
    zipCodes: string[],
    orgContractorIds: string[],
    defaultTemplateId?: string | null,
    defaultManagerIds?: string[],
  ) => Promise<void>;
  addMarket?: (
    name: string,
    zipCodes: string[],
    orgContractorIds: string[],
    defaultTemplateId?: string | null,
    defaultManagerIds?: string[],
  ) => Promise<ApiOrgMarket>;
  updateUsersForMarket: (marketId: string, userIds: string[]) => Promise<void>;
  readOnly?: boolean;
}) => {
  const { contractors } = useOrgContractors();
  const { users } = useUsers();
  const { users: usersAssigned } = useUsersByOrgMarketId(marketToEdit?.id);
  const { showToast, showErrorToast } = useToast();
  const { scanTemplates } = useScanTemplates();
  const activeTemplates = scanTemplates?.filter(template => !template.archivedAt);
  return (
    <Formik<
      { name: string; zipCodes: string[]; orgContractorIds: string[]; userIds: string[]; defaultTemplateId?: string | null; defaultManagerIds: string[] }
    >
      initialValues={{
        name: marketToEdit?.name || '',
        zipCodes: (marketToEdit as any)?.zipCodes || [],
        orgContractorIds: marketToEdit?.marketContractors?.map(c => c.orgContractor.id) || [],
        userIds: usersAssigned?.map(u => u.id) || [],
        defaultTemplateId: marketToEdit?.defaultTemplateId || '',
        defaultManagerIds: marketToEdit?.defaultManagerIds || [],
      }}
      enableReinitialize
      onSubmit={async values => {
        const isFormValid = values.name && values.zipCodes.length > 0;
        if (!isFormValid || readOnly) return;

        try {
          if (marketToEdit) {
            await updateMarket(
              marketToEdit.id,
              values.name,
              values.zipCodes,
              values.orgContractorIds,
              values.defaultTemplateId || null,
              values.defaultManagerIds,
            );
            await updateUsersForMarket(marketToEdit.id, values.userIds);
            showToast('Market updated successfully', 'success');
          } else {
            if (!addMarket) return;
            const created = await addMarket(
              values.name,
              values.zipCodes,
              values.orgContractorIds,
              values.defaultTemplateId || null,
              values.defaultManagerIds,
            );
            if (values.userIds && values.userIds.length > 0) await updateUsersForMarket(created.id, values.userIds);
            showToast('Market created successfully', 'success');
          }
          closeFlyout();
        } catch (error) {
          const errorWithStatus = error as ErrorWithStatus;
          showErrorToast(errorWithStatus, marketToEdit ? 'update a market' : 'create a market');
        }
      }}
    >
      {({ handleSubmit, values }) => (
        <FlyOut
          closeButtonCss={styles.closeButton}
          contentCss={styles.content}
          drawerCss={styles.drawer}
          flyOutClosed={closeFlyout}
          footerCss={styles.footer}
          headerCss={styles.header}
          open={flyoutOpen}
          title={<MarketPanelTitle marketName={marketToEdit?.name || 'New market'} styles={styles} />}
          footer={!readOnly && (
            <div css={styles.actions}>
              <button css={styles.cancelAction} type='button' onClick={closeFlyout}>Cancel</button>
              <button
                css={styles.submitAction}
                type='button'
                disabled={!values.name || values.zipCodes.length === 0}
                onClick={() => handleSubmit()}
              >
                {marketToEdit ? 'Update Market' : 'Add Market'}
              </button>
            </div>
          )}
        >
          <form css={styles.form} onSubmit={handleSubmit}>
            <section css={[styles.section, styles.sectionFirst]}>
              <MarketField label='Name' styles={styles}>
                <FormikLabeledInput
                  name='name'
                  placeholder='Enter a market name'
                  size='large'
                  backgroundColor='var(--background)'
                  disabled={readOnly}
                />
              </MarketField>
              <MarketField label='Zip codes' styles={styles}>
                <FormikLabeledMultiSelect
                  name='zipCodes'
                  placeholder='Type a zip code, then Enter'
                  size='large'
                  backgroundColor='var(--background)'
                  allowAddNew={!readOnly}
                  isNewItemValid={isZipCodeValid}
                  addButtonLabel='Add Zip Code'
                  disabled={readOnly}
                />
              </MarketField>
            </section>
            <section css={styles.section}>
              <h2 css={styles.sectionTitle}>Defaults for new jobs</h2>
              {activeTemplates && (
                <MarketField label='Job template' optional styles={styles}>
                  <FormikLabeledSelect
                    name='defaultTemplateId'
                    placeholder='No template'
                    size='large'
                    backgroundColor='var(--background)'
                    items={[
                      { value: '', label: 'None' },
                      ...activeTemplates.map(template => ({ value: template.id, label: template.name })),
                    ]}
                    disabled={readOnly}
                  />
                </MarketField>
              )}
              {users && (
                <MarketField label='Job managers' optional styles={styles}>
                  <FormikLabeledMultiSelect
                    name='defaultManagerIds'
                    placeholder='Add a manager...'
                    size='large'
                    backgroundColor='var(--background)'
                    items={users.map(user => ({ value: user.id, label: user.name || user.phoneNumber || user.email || `User ${user.id}` }))}
                    disabled={readOnly}
                  />
                </MarketField>
              )}
            </section>
            <section css={[styles.section, styles.sectionLast]}>
              <h2 css={styles.sectionTitle}>Who works here</h2>
              {contractors && (
                <MarketField label='Contractors' optional styles={styles}>
                  <FormikLabeledMultiSelect
                    name='orgContractorIds'
                    placeholder='Add a contractor...'
                    size='large'
                    backgroundColor='var(--background)'
                    items={contractors.map(contractor => ({ value: contractor.id, label: contractor.name }))}
                    disabled={readOnly}
                  />
                </MarketField>
              )}
              {users && (
                <MarketField label='Users' optional styles={styles}>
                  <FormikLabeledMultiSelect
                    name='userIds'
                    placeholder='Add a user...'
                    size='large'
                    backgroundColor='var(--background)'
                    items={users.map(user => ({ value: user.id, label: user.name || user.phoneNumber || user.email || `User ${user.id}` }))}
                    disabled={readOnly}
                  />
                </MarketField>
              )}
            </section>
          </form>
        </FlyOut>
      )}
    </Formik>
  );
};
